import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GridPattern } from "@/components/ui/grid-pattern";
import { cn } from "@/lib/utils";
import { ResponsiveImage } from "@/components/primitives/responsive-image";

const expoOut = [0.16, 1, 0.3, 1] as const;

/**
 * Hardcoded hero clients — 10 logos, each with a case study result + background image.
 * These rarely change. Update here when swapping a client.
 */
// Logo paths use .webp variants (which all exist on disk) for ~210 KB
// savings on initial paint of the homepage hero. Notable: sports-illustrated
// drops 114 KB → 3 KB; nasdaq 71 KB → 35 KB; kkr 36 KB → 10 KB. with-clarity
// stays as .png because its webp is slightly larger than the source.
const HERO_CLIENTS = [
  {
    name: "Forbes",
    logo: "/images/client-logos/forbes-2.webp",
    bgImage: "/images/case-studies/20-increase-in-subscribers-for-forbes-magazine.avif",
    result: "20% increase in digital subscribers through AI-powered paid media and CRO.",
  },
  {
    name: "Nasdaq",
    logo: "/images/client-logos/nasdaq.webp",
    bgImage: "/images/case-studies/optimized-lead-quality-generates-270-increase-in-sqls-for-nasdaq.avif",
    result: "270% increase in sales-qualified leads with optimized lead quality campaigns.",
  },
  {
    name: "Grown Brilliance",
    logo: "/images/client-logos/grown-brilliance.webp",
    bgImage: "/images/case-studies/adventure-helps-luxury-jewelry-brand-go-from-0-to-200-000-000-in-revenue-in-18-months.avif",
    result: "$0 to $200M in revenue in 18 months with full-funnel paid media.",
  },
  {
    name: "AMC Networks",
    logo: "/images/client-logos/amc-networks.webp",
    bgImage: "/images/case-studies/amc-networks-hero.jpg",
    result: "Scaled streaming subscriber acquisition across multiple AMC+ campaigns.",
  },
  {
    name: "Sennheiser",
    logo: "/images/client-logos/sennheiser.webp",
    bgImage: "/images/case-studies/sennheiser-live-hero.jpg",
    result: "Messaging optimization drove breakthrough creative performance for premium audio.",
  },
  {
    name: "KKR",
    logo: "/images/client-logos/kkr.webp",
    bgImage: "/images/case-studies/machine-learning-crm-integration-help-scale-saas-company.avif",
    result: "Portfolio company growth through machine learning and CRM-integrated campaigns.",
  },
  {
    name: "Sports Illustrated",
    logo: "/images/client-logos/sports-illustrated.webp",
    bgImage: "/images/case-studies/si-covers-hero.jpg",
    result: "Maximized subscriber LTV with data-driven acquisition and retention strategy.",
  },
  {
    name: "Adrianna Papell",
    logo: "/images/client-logos/adrianna-papell.avif",
    bgImage: "/images/case-studies/adrianna-papell-hero.jpg",
    result: "Bold BFCM strategy supercharged seasonal sales for a legacy fashion brand.",
  },
  {
    name: "Slinger Bag",
    logo: "/images/client-logos/slinger-bag.webp",
    bgImage: "/images/case-studies/adventure-media-brings-consumer-sports-good-product-to-market-leveraging-facebook-ecommerce-to-increase-branded-traffic-and-bof-searches.avif",
    result: "12.6X Facebook ROAS — became the #1 tennis ball launcher in the US.",
  },
  {
    name: "With Clarity",
    logo: "/images/client-logos/with-clarity.png",
    bgImage: "/images/case-studies/with-clarity-jewelry-hero.jpg",
    result: "1,200% ROI through remarketing campaigns for luxury lab-grown diamonds.",
  },
];

// Split into two offset columns
const COL_A = HERO_CLIENTS.filter((_, i) => i % 2 === 0);
const COL_B = HERO_CLIENTS.filter((_, i) => i % 2 === 1);


/**
 * Ethereal background image that appears on logo hover.
 * Uses layered radial-gradient masks for a soft, irregular, cosmic fade.
 */
function HoverBackground({ client }: { client: (typeof HERO_CLIENTS)[number] | null }) {
  const maskValue = [
    "radial-gradient(ellipse 85% 75% at 55% 48%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.18) 35%, rgba(0,0,0,0.06) 55%, transparent 72%)",
    "radial-gradient(ellipse 60% 90% at 45% 55%, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.10) 40%, transparent 65%)",
    "radial-gradient(ellipse 100% 50% at 50% 45%, rgba(0,0,0,0.15) 0%, transparent 60%)",
  ].join(", ");

  return (
    <AnimatePresence>
      {client && (
        <motion.div
          key={client.name}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: expoOut }}
          className="absolute inset-0 z-[1]"
          style={{
            maskImage: maskValue,
            maskComposite: "add",
            WebkitMaskImage: maskValue,
            WebkitMaskComposite: "source-over",
          }}
        >
          <ResponsiveImage
            src={client.bgImage}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden={true}
            width={1920}
            height={1080}
            sizes="100vw"
            priority
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Result box — larger callout with logo and result statement.
 */
function ResultBox({ client }: { client: (typeof HERO_CLIENTS)[number] | null }) {
  return (
    <AnimatePresence>
      {client && (
        <motion.div
          key={client.name}
          initial={{ opacity: 0, y: 12, scaleY: 0.94 }}
          animate={{ opacity: 1, y: 0, scaleY: 1 }}
          exit={{ opacity: 0, y: 6, scaleY: 0.97 }}
          transition={{ duration: 0.35, ease: expoOut }}
          className="hidden lg:block absolute bottom-16 right-12 z-[5] w-[400px] border border-charcoal/20 bg-cream/92 backdrop-blur-sm p-8 origin-bottom pointer-events-none"
        >
          <img
            src={client.logo}
            alt={client.name}
            className="h-8 w-auto object-contain mb-4"
            width={160}
            height={32}
          />
          <p className="type-body text-charcoal/80 leading-relaxed">
            {client.result}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Single logo — paints visible immediately. Fade-out on other-logo hover is
 * driven by motion (smooth 0.4s); return-to-visible is instant. The previous
 * scattered-entrance animation was removed because it caused the logos to be
 * SSR'd at opacity:0 — they then "popped in" after hydration, which read as
 * a flash on first paint.
 */
function LogoItem({
  client,
  isHovered,
  isAnyHovered,
  onHover,
  onLeave,
}: {
  client: (typeof HERO_CLIENTS)[number];
  isHovered: boolean;
  isAnyHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}) {
  const targetOpacity = isAnyHovered && !isHovered ? 0 : 1;
  const duration = targetOpacity === 0 ? 0.4 : 0;

  return (
    <motion.div
      initial={false}
      animate={{ opacity: targetOpacity }}
      transition={{ duration, ease: expoOut }}
      className="flex items-center justify-center cursor-pointer"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <div className="w-[120px] h-[48px] md:w-[140px] md:h-[52px] lg:w-[160px] lg:h-[56px] flex items-center justify-center">
        <img
          src={client.logo}
          alt={client.name}
          className={cn(
            "max-w-full max-h-full w-auto h-auto object-contain transition-all duration-500",
            isHovered
              ? "grayscale-0 opacity-100"
              : "grayscale opacity-30",
          )}
          loading="lazy"
          fetchPriority="low"
          width={160}
          height={56}
        />
      </div>
    </motion.div>
  );
}

export function Hero() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const hoveredClient = hoveredIndex !== null ? HERO_CLIENTS[hoveredIndex] : null;
  const isAnyHovered = hoveredIndex !== null;

  const handleHover = useCallback((i: number) => setHoveredIndex(i), []);
  const handleLeave = useCallback(() => setHoveredIndex(null), []);

  return (
    <section
      className="relative min-h-screen w-full flex flex-col justify-center bg-cream overflow-hidden"
      data-testid="section-hero"
    >
      {/* Layer 1: Hover background image */}
      <HoverBackground client={hoveredClient} />

      {/* Layer 2: Grid pattern — sits above BG image, below content */}
      <GridPattern
        width={24}
        height={24}
        x={-1}
        y={-1}
        className={cn(
          "z-[2] fill-transparent stroke-[hsla(60,3%,8%,0.14)]",
          "[mask-image:linear-gradient(to_top_left,rgba(0,0,0,0.6)_0%,rgba(0,0,0,0.25)_40%,transparent_70%)]",
        )}
      />

      {/* Layer 3: Content — isolated stacking context */}
      <div className="relative z-[3] w-full pt-32 pb-16 lg:py-0" style={{ isolation: "isolate" }}>
        <div className="container-content flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-8">
          {/* Left: Copy + CTAs */}
          <div className="w-full lg:w-[55%] flex flex-col items-start">
            {/* h1 is the LCP element — render plain, not motion-wrapped, so
                Lighthouse sees a stable contentful paint at first frame
                rather than waiting for translateY animation to settle. */}
            <h1
              className="type-h2 text-charcoal max-w-[600px]"
              data-testid="text-hero-headline"
            >
              Performance Marketing & AI Transformation Agency: Elite Strategy with AI Rocketfuel
            </h1>

            <p
              className="type-body text-charcoal mt-8 mb-10 max-w-[520px]"
              data-testid="text-hero-subtitle"
            >
              We combine elite strategists with proprietary AI to deliver
              results other agencies can't touch — across Google, Meta, and
              every channel that matters.
            </p>

            <div
              className="flex flex-col sm:flex-row items-start gap-4"
            >
              <a href="#services-explorer">
                <button className="btn btn-cta btn-lg" data-testid="button-hero-cta">
                  Services
                </button>
              </a>
              <a href="#case-studies">
                <button className="btn btn-outline btn-lg" data-testid="button-hero-explore">
                  Our Results
                </button>
              </a>
            </div>
          </div>

          {/* Mobile/tablet logos — 2 rows of 3 beneath CTAs */}
          <div className="lg:hidden w-full mt-2">
            <div className="grid grid-cols-3 gap-x-4 gap-y-5">
              {HERO_CLIENTS.slice(0, 6).map((client) => (
                <div
                  key={client.name}
                  className="flex items-center justify-center"
                >
                  <div className="w-[90px] h-[36px] flex items-center justify-center">
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="max-w-full max-h-full w-auto h-auto object-contain grayscale opacity-30"
                      loading="lazy"
                      width={90}
                      height={36}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Spacer so left content doesn't overlap the absolutely-positioned logos */}
          <div className="hidden lg:block lg:w-[45%]" />
        </div>
      </div>

      {/* Desktop logo columns — absolutely positioned, spanning full hero height */}
      <div className="hidden lg:flex absolute top-0 bottom-0 right-[8%] xl:right-[14%] z-[3] gap-16 xl:gap-20 py-20">
        {/* Column A — runs full height */}
        <div className="flex flex-col justify-between h-full">
          {COL_A.map((client) => {
            const idx = HERO_CLIENTS.indexOf(client);
            return (
              <LogoItem
                key={client.name}
                client={client}
                isHovered={hoveredIndex === idx}
                isAnyHovered={isAnyHovered}
                onHover={() => handleHover(idx)}
                onLeave={handleLeave}
              />
            );
          })}
        </div>
        {/* Column B — offset down significantly for alternating stagger */}
        <div className="flex flex-col justify-between h-full pt-16 pb-0">
          {COL_B.map((client) => {
            const idx = HERO_CLIENTS.indexOf(client);
            return (
              <LogoItem
                key={client.name}
                client={client}
                isHovered={hoveredIndex === idx}
                isAnyHovered={isAnyHovered}
                onHover={() => handleHover(idx)}
                onLeave={handleLeave}
              />
            );
          })}
        </div>
      </div>

      {/* Result box — positioned bottom-right, desktop only */}
      <ResultBox client={hoveredClient} />
    </section>
  );
}
